import * as React from "react";
import { Box, Card, CardContent, CardHeader, TextField, InputAdornment, IconButton, LinearProgress, Typography, Button, Stack, Snackbar, Alert, } from "@mui/material";
import { Visibility, VisibilityOff, Lock } from "@mui/icons-material";
import { changePassword, getOldPassword } from '../../features/auth/authSlice'
import { useDispatch, useSelector } from "react-redux";
import ModernNavbar from "../setup/ModernNavbar";
// --- Utility: Simple password strength (0-100) ---
function getPasswordStrength(pw = "") {
    let score = 0;
    const len = pw.length;

    if (len >= 8) score += 25;
    if (len >= 12) score += 15;

    if (/[a-z]/.test(pw)) score += 15;
    if (/[A-Z]/.test(pw)) score += 15;
    if (/\d/.test(pw)) score += 15;
    if (/[^A-Za-z0-9]/.test(pw)) score += 15;

    return Math.min(score, 100);
}

export default function ChangePasswordForm({ onSubmitApi }) {

    // top pe
    const CURRENT_INCORRECT = "incorrect";

    const { user } = useSelector((state) => state.auth)
    const dispatch = useDispatch();
    /**
     * Props:
     * onSubmitApi?: async function({ currentPassword, newPassword }) => { ok: boolean, message?: string }
     */

    const [values, setValues] = React.useState({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
    });

    const [show, setShow] = React.useState({
        current: false,
        next: false,
        confirm: false,
    });

    const [errors, setErrors] = React.useState({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
    });

    const [submitting, setSubmitting] = React.useState(false);
    const [toast, setToast] = React.useState({
        open: false,
        severity: "success",
        message: "",
    });

    const strength = getPasswordStrength(values.newPassword);
    const strengthLabel =
        strength < 40 ? "Weak" : strength < 70 ? "Medium" : "Strong";

    const setField = (name, val) => {
        setValues((v) => ({ ...v, [name]: val }));
        // clear inline error while typing
        setErrors((e) => ({ ...e, [name]: "" }));
        if (name === "confirmPassword") {
            // live confirm check
            if (val !== values.newPassword) {
                setErrors((e) => ({
                    ...e,
                    confirmPassword: "New & Confirm password match nahi kar rahe.",
                }));
            }
        }
    };

    const validate = () => {
        const e = { currentPassword: "", newPassword: "", confirmPassword: "" };

        // Agar pehle se incorrect flag hai to preserve kar do
        if (errors.currentPassword === CURRENT_INCORRECT) {
            e.currentPassword = CURRENT_INCORRECT;
        } else {
            if (!values.currentPassword || values.currentPassword.length < 4) {
                e.currentPassword = "Current password required (min 4 chars).";
            }
        }

        const pw = values.newPassword || "";
        if (!pw) {
            e.newPassword = "Naya password zaroori hai.";
        } else {
            if (pw.length < 8) e.newPassword = "Kam az kam 8 characters.";
            const hasAll = /[A-Z]/.test(pw) && /[a-z]/.test(pw) && /\d/.test(pw) && /[^A-Za-z0-9]/.test(pw);
            if (!hasAll && !e.newPassword) e.newPassword = "Aa, 0-9, aur koi symbol include karein.";
        }

        if (!values.confirmPassword) {
            e.confirmPassword = "Confirm password required hai.";
        } else if (values.confirmPassword !== values.newPassword) {
            e.confirmPassword = "New & Confirm password match nahi kar rahe.";
        }

        setErrors(e);
        return !e.currentPassword && !e.newPassword && !e.confirmPassword;
    };

    // const mockSubmit = async () => {
    //     await new Promise((r) => setTimeout(r, 500));
    //     return { ok: true, message: "Password successfully changed." };
    // };


    const handleSubmit = async (e) => {
        e.preventDefault();

        // agar current incorrect flag laga hua hai to yahin rok do
        if (errors.currentPassword === "incorrect") {
            setToast({ open: true, severity: "error", message: "Current password is incorrect" });
            return;
        }

        // basic form validations
        if (!validate()) return;

        try {
            // ✅ ACTUAL REDUX DISPATCH
            setSubmitting(true);
            const resultAction = await dispatch(
                changePassword({
                    userPassword: values.newPassword, userName: user.userName,
                })
            );

            // Option A: unwrap (simple)
            // const msg = await dispatch(changePassword({ currentPassword: values.currentPassword, newPassword: values.newPassword })).unwrap();

            // Option B: match-based handling (explicit)
            if (changePassword.fulfilled.match(resultAction)) {
                const msg = resultAction.payload || "Password changed successfully!";
                setToast({ open: true, severity: "success", message: msg });
                setValues({ currentPassword: "", newPassword: "", confirmPassword: "" });
                setSubmitting(false)
            } else {
                const errMsg =
                    resultAction.payload || resultAction.error?.message || "Failed to change password.";
                setToast({ open: true, severity: "error", message: errMsg });
            }
        } catch (err) {
            setToast({
                open: true,
                severity: "error",
                message: err?.message || "Something went wrong.",
            });
        }
    };

    const disabled =
        submitting ||
        !values.newPassword ||
        !values.confirmPassword ||
        values.newPassword !== values.confirmPassword ||
        strength < 40;

    const funcGetOldPassword = async () => {
        dispatch(getOldPassword({
            userName: user.userName,
            userPassword: values.currentPassword
        })).then((res) => {
            if (res?.payload?.userPassword) {
                // sahi password mila
                setErrors((e) => ({ ...e, currentPassword: "" }));
            } else {
                // GALAT: exact constant use karo (no dot)
                setErrors((e) => ({ ...e, currentPassword: CURRENT_INCORRECT }));
            }
        });
    };

    return (
        <>
            <ModernNavbar />
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <Card sx={{ maxWidth: 520 }}>
                    <CardHeader
                        avatar={<Lock />}
                        title="Change Password"
                        subheader="Please Use Strong password (12+ chars, mix of Aa, 0-9, symbols)"
                    />
                    <CardContent>
                        <Box component="form" onSubmit={handleSubmit}>
                            <Stack spacing={2.5}>
                                {/* Current Password */}
                                <TextField
                                    label="Current Password"
                                    type={show.current ? "text" : "password"}
                                    fullWidth
                                    autoComplete="current-password"
                                    value={values.currentPassword}
                                    onChange={(e) => setField("currentPassword", e.target.value)}
                                    error={!!errors.currentPassword}
                                    helperText={errors.currentPassword}
                                    onBlur={(e) => { funcGetOldPassword(e); }}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() =>
                                                        setShow((s) => ({ ...s, current: !s.current }))
                                                    }
                                                    edge="end"
                                                    aria-label="toggle current password visibility"
                                                >
                                                    {show.current ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />

                                {/* New Password */}
                                <TextField
                                    label="New Password"
                                    type={show.next ? "text" : "password"}
                                    fullWidth
                                    autoComplete="new-password"
                                    value={values.newPassword}
                                    onChange={(e) => setField("newPassword", e.target.value)}
                                    error={!!errors.newPassword}
                                    helperText={errors.newPassword || "8 chars + Aa, 0-9, symbol"}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => setShow((s) => ({ ...s, next: !s.next }))}
                                                    edge="end"
                                                    aria-label="toggle new password visibility"
                                                >
                                                    {show.next ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />

                                {/* Strength meter */}
                                <Box>
                                    <Stack direction="row" alignItems="center" spacing={2}>
                                        <Box sx={{ flex: 1 }}>
                                            <LinearProgress
                                                variant="determinate"
                                                value={strength}
                                                sx={{ height: 8, borderRadius: 1 }}
                                            />
                                        </Box>
                                        <Typography variant="body2" sx={{ minWidth: 60 }}>
                                            {strengthLabel}
                                        </Typography>
                                    </Stack>
                                    <Typography variant="caption" sx={{ opacity: 0.75 }}>
                                        Tips: 12+ length, Upper/Lower, number, symbol.
                                    </Typography>
                                </Box>

                                {/* Confirm Password */}
                                <TextField
                                    label="Confirm New Password"
                                    type={show.confirm ? "text" : "password"}
                                    fullWidth
                                    autoComplete="new-password"
                                    value={values.confirmPassword}
                                    onChange={(e) => setField("confirmPassword", e.target.value)}
                                    error={!!errors.confirmPassword}
                                    helperText={errors.confirmPassword}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() =>
                                                        setShow((s) => ({ ...s, confirm: !s.confirm }))
                                                    }
                                                    edge="end"
                                                    aria-label="toggle confirm password visibility"
                                                >
                                                    {show.confirm ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />

                                <Stack direction="row" spacing={2} justifyContent="flex-end">
                                    <Button
                                        type="button"
                                        variant="outlined"
                                        onClick={() => {
                                            setValues({ currentPassword: "", newPassword: "", confirmPassword: "" });
                                            setErrors({ currentPassword: "", newPassword: "", confirmPassword: "" });
                                        }}
                                        disabled={submitting}
                                    >
                                        Reset
                                    </Button>
                                    <Button type="submit" variant="contained" disabled={disabled}>
                                        {submitting ? "Saving..." : "Change Password"}
                                    </Button>
                                </Stack>

                                <Typography variant="caption" color="text.secondary">
                                    Note: Button "Medium" strength ho (≈40%+).
                                </Typography>
                            </Stack>
                        </Box>
                    </CardContent>

                    <Snackbar
                        open={toast.open}
                        autoHideDuration={3500}
                        onClose={() => setToast((t) => ({ ...t, open: false }))}
                        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                    >
                        <Alert
                            onClose={() => setToast((t) => ({ ...t, open: false }))}
                            severity={toast.severity}
                            variant="filled"
                            sx={{ width: "100%" }}
                        >
                            {toast.message}
                        </Alert>
                    </Snackbar>
                </Card>
            </Box>
        </>
    );
}
